/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */


import {IconButton, Typography} from "@mui/material"
import {Header} from "../../../pages/CoverageRequirements/Table/Dialogs/constants"
import {Container} from "../../../pages/CoverageRequirements/Table/constants"
import ArgosDialogHeader from "../ArgosDialogHeader"
import ArgosButton from "../../Buttons/ArgosButton"
import {useAppDispatch, useAppSelector} from "../../../../redux/hooks"
import {selectDialogState, setIsOverrideVersionDataWarningDialogOpen} from "../../../../redux/ducks/VehicleProgramsDetails/slice"
import CloseIcon from '@mui/icons-material/Close';
import ArgosFlexbox from "../../Containers/ArgosFlexbox"
import * as React from "react";
import {useEffect, useMemo} from "react";
import {MRT_ColumnDef} from "material-react-table";
import {COLUMN_HEADER_CONTAINED_IN_VEHICLE_PROGRAM_DEVELOPMENT_STAGE} from "../../../pages/vehiclePrograms/ItemDetails/constants";
import {ArgosTableSimple} from "../../Table/ArgosTableSimple";

const duplicateSensorsErrorMsg = 'We have detected more than 1 sensor of the same modality at the same location (shown in red). This configuration is not currently allowed. Please correct and reupload.'

const OverrideDataWarningDialog = (props: any) => {
  const dispatch = useAppDispatch()
  const dialogData = useAppSelector(state => selectDialogState(state).overrideDialogData)
  const invalidSensors = dialogData?.invalidSensors;
  const hasDuplicates = dialogData?.duplicateSensors && dialogData?.duplicateSensors?.tableRows?.length > 0

  const [tableRows, setTableRows] = React.useState<any[]>([])
  useEffect(() => {
    if (hasDuplicates) {
      setTableRows(dialogData.duplicateSensors.tableRows)
    } else if (invalidSensors) {
      setTableRows(invalidSensors?.tableRows ?? [])
    } else {
      setTableRows([])
    }
  }, [dialogData, hasDuplicates]);

  const onClickCancel = () => {
    if (props?.onClickCancel)
      props?.onClickCancel()

    dispatch(setIsOverrideVersionDataWarningDialogOpen(false))
  }

  const onClickContinue = () => {
    dispatch(setIsOverrideVersionDataWarningDialogOpen(false))
  }

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => {
      if (hasDuplicates) {
        return [
          {
            accessorKey: 'sensorName',
            id: 'sensorName',
            header: 'Sensor Name',
          },
          {
            accessorKey: 'sensorType',
            id: 'sensorType',
            header: 'Sensor Type',
          },
          {
            accessorKey: 'sensorLocation',
            id: 'sensorLocation',
            header: 'Sensor Location',
          },
        ];
      } else {
        return [
          {
            accessorKey: 'adasProgramName',
            id: 'adasProgramName',
            header: 'ADAS Program Name',
            size: 300,
          },
          {
            accessorKey: 'sensorName',
            id: 'sensorName',
            header: 'Sensor Name',
          },
          {
            accessorKey: 'sensorType',
            id: 'sensorType',
            header: 'Sensor Type',
          },
          {
            accessorKey: 'sensorLocation',
            id: 'sensorLocation',
            header: 'Sensor Location',
          },
          {
            accessorFn: (originalRow) => originalRow.inDevStage?.toString() ?? '',
            id: 'inDevStage',
            header: COLUMN_HEADER_CONTAINED_IN_VEHICLE_PROGRAM_DEVELOPMENT_STAGE,
          },
        ];
      }
    }, [hasDuplicates]);

  const hasInvalidSensors = dialogData?.invalidSensors && dialogData?.invalidSensors?.missingSensors && Object.keys(dialogData?.invalidSensors?.missingSensors).length > 0

  let confirmText = `You are overwriting data for this vehicle program development stage. Do you want to continue?`
  let headerText = `Warning! Potential data loss`
  if (hasInvalidSensors) {
    confirmText = `The sensor modality/location data you are about to replace is in conflict with one more or linked ADAS programs.
     Sensors listed in red are defined in the ADAS program but not in the vehicle program development stage. Do you want to continue? If so, the ADAS
     programs with conflicts will be removed.`
  } else if (dialogData.duplicateSensors?.tableRows) {
    confirmText = duplicateSensorsErrorMsg
    headerText = 'Duplicate sensors detected'
  }

  return (
    <Container>
      <ArgosDialogHeader>
        <ArgosFlexbox style={{justifyContent: 'space-between', alignItems: 'center'}}>
          <Header>
            <div style={{
              color: 'white',
              paddingLeft: 5,
              fontSize: 14
            }}>
              {headerText}
            </div>
          </Header>
          {!hasDuplicates &&
            <IconButton onClick={onClickCancel} sx={{color: 'white', padding: 0, fontSize: 'small'}}>
              <CloseIcon/>
            </IconButton>
          }
        </ArgosFlexbox>
      </ArgosDialogHeader>
      <ArgosFlexbox vertical style={{overflow: 'auto', padding: 10}}>
        <Typography variant="h6" style={{alignSelf: "center", width: "80%", padding: "4px"}}>
          {confirmText}
        </Typography>
        {tableRows && <ArgosTableSimple
          data={tableRows ?? []}
          columns={columns}

          getSubRows={(originalRow) => originalRow?.subRows}
          enableExpanding

          muiTableBodyRowProps={({row}) => ({
            //conditionally style selected rows
            sx: {
              backgroundColor: hasDuplicates ? (row.original.isDuplicate ? '#FF7276' : '') :
                row.original.inDevStage === true ? 'lightgreen' : row.original.inDevStage === false ? '#FF7276' : '',
            },
          })}

          initialState={{
            density: 'compact',
            expanded: true,
          }}
        />}
      </ArgosFlexbox>
      <div style={{alignSelf: 'center', display: 'flex', gap: 10, marginTop: 'auto', marginBottom: 10}}>
        {!hasDuplicates &&
          <ArgosButton
            onClick={onClickCancel}
          >
            Cancel
          </ArgosButton>
        }
        <ArgosButton
          onClick={onClickContinue}
        >
          OK
        </ArgosButton>
      </div>
    </Container>
  )
}
export {OverrideDataWarningDialog}